import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RefreshCw } from "lucide-react";

const CLASSES = ["CMT4", "CMT5", "CMT6", "PST4", "PST5", "PST6", "SW4", "SW5", "SW6"];
const SEMESTERS = ["I", "II"];

export function SessionCoverageTab() {
  const [semester, setSemester] = useState<string>("");
  const [klass, setKlass] = useState<string>("");

  const enabled = !!semester && !!klass;

  const { data: moduleSessions = [], isFetching: loadingMS, refetch: refetchMS } = useQuery({
    queryKey: ["coverage-module-sessions", semester, klass],
    enabled,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_sessions")
        .select("id, module_code, module_name")
        .eq("semester", semester)
        .eq("class", klass)
        .eq("active", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  const moduleSessionIds = useMemo(() => moduleSessions.map((m: any) => m.id), [moduleSessions]);

  const { data: covered = [], isFetching: loadingCov, refetch: refetchCov } = useQuery({
    queryKey: ["coverage-attendance-sessions", moduleSessionIds.join(",")],
    enabled: enabled && moduleSessionIds.length > 0,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendance_sessions")
        .select("module_session_id")
        .in("module_session_id", moduleSessionIds)
        .eq("status", "closed")
        .eq("excluded_from_coverage", false);
      if (error) throw error;
      return data ?? [];
    },
  });

  const rows = useMemo(() => {
    const byModule = new Map<string, { code: string; name: string | null; total: number; coveredIds: Set<string> }>();
    moduleSessions.forEach((ms: any) => {
      const key = ms.module_code;
      const entry = byModule.get(key) ?? { code: ms.module_code, name: ms.module_name, total: 0, coveredIds: new Set() };
      entry.total += 1;
      byModule.set(key, entry);
    });
    // Map ms id -> module_code
    const idToCode = new Map<string, string>(moduleSessions.map((m: any) => [m.id, m.module_code]));
    covered.forEach((c: any) => {
      const code = idToCode.get(c.module_session_id);
      if (!code) return;
      byModule.get(code)?.coveredIds.add(c.module_session_id);
    });
    return Array.from(byModule.values())
      .map(e => {
        const cov = e.coveredIds.size;
        const pct = e.total > 0 ? Math.round((cov / e.total) * 100) : 0;
        return { ...e, covered: cov, pending: e.total - cov, pctCovered: pct, pctPending: 100 - pct };
      })
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [moduleSessions, covered]);

  const loading = loadingMS || loadingCov;

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Session Coverage</CardTitle>
          <CardDescription>Compare registered sessions against scanned (closed) sessions per module.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Semester</Label>
              <Select value={semester} onValueChange={setSemester}>
                <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                <SelectContent position="popper">
                  {SEMESTERS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class</Label>
              <Select value={klass} onValueChange={setKlass}>
                <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent position="popper">
                  {CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => { refetchMS(); refetchCov(); }} disabled={!enabled}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
            </div>
          </div>

          {enabled && (
            <div className="table-responsive">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">SN</TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Covered</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={r.code}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <div className="font-mono font-medium">{r.code}</div>
                        {r.name && <div className="text-xs text-muted-foreground">{r.name}</div>}
                      </TableCell>
                      <TableCell className="text-right">{r.total}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="default">{r.covered} ({r.pctCovered}%)</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{r.pending} ({r.pctPending}%)</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        {loading ? "Loading..." : "No registered modules for this class/semester."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
